import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import type { DataType } from '@revolist/react-datagrid';

import { RevoGridRowId, RevoGridSelectionExtras } from './types';

export const defaultGetRowId = (row?: DataType): RevoGridRowId => String(row?.id ?? '');

const EMPTY_SELECTION: ReadonlySet<RevoGridRowId> = new Set<RevoGridRowId>();

type UseRevoGridSelectionArgs = {
  gridRef: MutableRefObject<HTMLRevoGridElement | null>;
  source: DataType[];
  selectable: boolean;
  selectedIds?: ReadonlySet<RevoGridRowId>;
  onSelectedIdsChange?: (selectedIds: Set<RevoGridRowId>) => void;
  getRowId: (row?: DataType) => RevoGridRowId;
};

type UseRevoGridSelectionResult = {
  selection?: RevoGridSelectionExtras;
  syncVisibleIds: () => void;
};

export const useRevoGridSelection = ({
  gridRef,
  source,
  selectable,
  selectedIds = EMPTY_SELECTION,
  onSelectedIdsChange,
  getRowId,
}: UseRevoGridSelectionArgs): UseRevoGridSelectionResult => {
  /**
   * Ids of the rows the grid is actually SHOWING, which is not the same thing as
   * `source`: a column filter trims rows out of the viewport without touching
   * the source array. `getVisibleSource()` is the honest answer but it is a
   * Promise and templates are synchronous, so it is mirrored into state and
   * refreshed whenever the visible set can change.
   */
  const [visibleIds, setVisibleIds] = useState<RevoGridRowId[]>([]);

  const readVisibleIds = useCallback(async (): Promise<RevoGridRowId[]> => {
    const grid = gridRef.current;

    const visible = grid ? ((await grid.getVisibleSource()) as DataType[]) : source;
    return visible.map((row) => getRowId(row));
  }, [gridRef, source, getRowId]);

  const syncVisibleIds = useCallback(() => {
    void readVisibleIds().then(setVisibleIds);
  }, [readVisibleIds]);

  useEffect(() => {
    if (selectable) {
      syncVisibleIds();
    }
  }, [selectable, syncVisibleIds]);

  const onToggleRow = useCallback(
    (id: RevoGridRowId) => {
      const next = new Set(selectedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onSelectedIdsChange?.(next);
    },
    [selectedIds, onSelectedIdsChange]
  );

  const onToggleAll = useCallback(() => {
    void readVisibleIds().then((ids) => {
      setVisibleIds(ids);

      const everyVisibleSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
      const next = new Set(selectedIds);
      ids.forEach((id) => (everyVisibleSelected ? next.delete(id) : next.add(id)));
      onSelectedIdsChange?.(next);
    });
  }, [readVisibleIds, selectedIds, onSelectedIdsChange]);

  const visibleSelectedCount = visibleIds.reduce(
    (total, id) => (selectedIds.has(id) ? total + 1 : total),
    0
  );

  const selection = useMemo<RevoGridSelectionExtras>(
    () => ({
      selectedIds,
      allSelected: visibleIds.length > 0 && visibleSelectedCount === visibleIds.length,
      someSelected: visibleSelectedCount > 0,
      getRowId,
      onToggleRow,
      onToggleAll,
    }),
    [selectedIds, visibleIds.length, visibleSelectedCount, getRowId, onToggleRow, onToggleAll]
  );

  return { selection: selectable ? selection : undefined, syncVisibleIds };
};
